import React, { useEffect, useState } from 'react';
import GEVInput from './GEVInput';
import GEVYesNo from './GEVYesNo';
import GEVTexttarea from './GEVTexttarea';
import GEVGrainLicenseType from './GEVGrainLicenseType';
import GEVPerCrop from './GEVPerCrop';
import GEVPercentageOfThroughput from './GEVPercentageOfThroughput';
import { OperationProps } from '../../types/assessment_info/gev/BusinessOperationFormProps';
import { FormError, useFormErrorContext } from '../../utils/context/FormErrorContext';
import ErrorMessages from '../common/ErrorMessages/ErrorMessages';
import { BusinessOperationsKeys } from '../../pages/BusinessOperations/BusinessOperations';
import { useBusinessOperationsState } from '../../utils/context/BusinessOperationsContext';
import { FarmingOperationCommercialGrainClass } from '../../types/assessment_info/gev/BusinessOperationsData';

export default function FarmingOperationCommercialGrain(props: OperationProps): JSX.Element {
    const { errors, removeError } = useFormErrorContext();
    const { formData } = useBusinessOperationsState();
    const [formErrors, setFormErrors] = useState<FormError[]>([]);
    const { operation, incrementErrCount, resetErrCount } = props;

    const isTruckScaleOnSiteValue = (formData[operation as BusinessOperationsKeys] as FarmingOperationCommercialGrainClass)['isTruckScaleOnSite'];

    const areAnyBuildingsAddedOrRemovedValue = (formData[operation as BusinessOperationsKeys] as FarmingOperationCommercialGrainClass)['areAnyBuildingsAddedOrRemoved'];

    useEffect(() => {
        if (errors.length > 0) {
            errors.forEach((error) => {
                if (error.primaryKey && error.primaryKey.split('.')[1] === operation) {
                    const splitKey = error.primaryKey.split('.');
                    splitKey.shift();
                    error.primaryKey = splitKey.join(".");
                    setFormErrors(prevData => [...prevData, error])
                    incrementErrCount(operation as BusinessOperationsKeys)
                }
            })
        }
        else {
            setFormErrors([]);
        }
    }, [errors, operation, incrementErrCount]);

    const removeFormError = (field: string, linkedField?: string) => {
        const error = formErrors.find(err => err.primaryKey.includes(field))
        if (error !== undefined) {
            removeError(error.primaryKey);
            const filteredFormError = formErrors.filter(err => !(err.primaryKey.includes(field)))
            if (filteredFormError.length === 0) {
                resetErrCount(operation as BusinessOperationsKeys);
            }
            setFormErrors(filteredFormError);
        }
        if (linkedField) {
            removeFormError(linkedField);
        }
    }

    return (
        <div>
            <ErrorMessages errors={formErrors} />
            <form>
                <table width={"100%"} role="none" className='gev-table'>
                    <tbody>
                        <GEVGrainLicenseType
                            index={1}
                            operation={operation}
                            name={'grainLicenseType'}
                            errors={props.errors}
                            hasErrors={formErrors.some(err => err.primaryKey.includes('grainLicenseType'))}
                            removeError={removeFormError}
                        />
                        <GEVInput
                            index={2}
                            operation={operation}
                            name={'licenseRegisteredTo'}
                            errors={props.errors}
                            hasErrors={formErrors.some(err => err.primaryKey.includes('licenseRegisteredTo'))}
                            removeError={removeFormError}
                        />
                        <GEVYesNo
                            index={3}
                            operation={operation}
                            name={'isLicenseInUse'}
                            errors={props.errors}
                            hasErrors={formErrors.some(err => err.primaryKey.includes('isLicenseInUse'))}
                            removeError={removeFormError}
                        />
                        <GEVInput
                            index={4}
                            operation={operation}
                            name={'totalBuschelCapacity'}
                            type="number"
                            errors={props.errors}
                            hasErrors={formErrors.some(err => err.primaryKey.includes('totalBuschelCapacity'))}
                            removeError={removeFormError}
                        />
                        <GEVInput
                            index={5}
                            operation={operation}
                            name={'totalAcresCropped'}
                            type="number"
                            errors={props.errors}
                            hasErrors={formErrors.some(err => err.primaryKey.includes('totalAcresCropped'))}
                            removeError={removeFormError}
                        />
                        <GEVPerCrop
                            index={6}
                            operation={operation}
                            name={'acresPerCrop'}
                            errors={props.errors}
                            hasErrors={formErrors.some(err => err.primaryKey.includes('acresPerCrop'))}
                            removeError={removeFormError}
                        />
                        <GEVPerCrop
                            index={7}
                            operation={operation}
                            name={'yieldPerCrop'}
                            errors={props.errors}
                            removeError={removeFormError}
                        />
                        <GEVYesNo
                            index={8}
                            operation={operation}
                            name={'isTruckScaleOnSite'}
                            errors={props.errors}
                            hasErrors={formErrors.some(err => err.primaryKey.includes('isTruckScaleOnSite'))}
                            removeError={removeFormError}
                        />
                        {(isTruckScaleOnSiteValue === true) && <GEVInput
                            operation={operation}
                            name={'truckScaleCapacity'}
                            errors={props.errors}
                            hasErrors={formErrors.some(err => err.primaryKey.includes('truckScaleCapacity'))}
                            removeError={removeFormError}
                        />}

                        <GEVPercentageOfThroughput
                            index={9}
                            operation={operation}
                            name={'percentageOfThroughput'}
                            errors={props.errors}
                            removeError={removeFormError}
                        />
                        <GEVYesNo
                            index={10}
                            operation={operation}
                            name={'areAnyBuildingsAddedOrRemoved'}
                            errors={props.errors}
                            hasErrors={formErrors.some(err => err.primaryKey.includes('areAnyBuildingsAddedOrRemoved'))}
                            removeError={removeFormError}
                        />
                        {areAnyBuildingsAddedOrRemovedValue &&
                            <GEVTexttarea
                                operation={operation}
                                name={'comments'}
                                errors={props.errors}
                                hasErrors={formErrors.some(err => err.primaryKey.includes('comments'))}
                                removeError={removeFormError}
                            />
                        }

                    </tbody>
                </table>
            </form>
        </div>
    );
}
